/**
 * Maintenance: what needs doing, and the button that does it.
 *
 * One card component renders every job from the server's registry, so a job added
 * on the server appears here with no change to this file. Each card leads with the
 * standing count -- the answer to "is there anything to do right now" -- and the
 * plain-language impact statement comes from the registry rather than being
 * restated here, where it would drift from the code that performs the work.
 */
import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Play, RefreshCw, ScrollText, ShieldAlert, XCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { MAINTENANCE_CATEGORIES } from "@shared/maintenanceCategories";
import MemberMessageReportsPanel from "@/components/MemberMessageReportsPanel";

type JobState = "ok" | "attention" | "error";

const STATE_STYLE: Record<JobState, { color: string; bg: string; border: string; label: string }> = {
  ok:        { color: "#22C55E", bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.28)",  label: "Clear" },
  attention: { color: "#F5A623", bg: "rgba(245,166,35,0.10)", border: "rgba(245,166,35,0.30)", label: "Needs attention" },
  error:     { color: "#EF4444", bg: "rgba(239,68,68,0.10)",  border: "rgba(239,68,68,0.28)",  label: "Broken" },
};

const KIND_LABEL: Record<string, string> = {
  check: "Read-only",
  write: "Writes rows",
  send: "Sends email",
  publish: "Publishes externally",
};

function StateIcon({ state }: { state: JobState }) {
  if (state === "ok") return <CheckCircle2 size={15} aria-hidden="true" />;
  if (state === "attention") return <AlertTriangle size={15} aria-hidden="true" />;
  return <XCircle size={15} aria-hidden="true" />;
}

const panel: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "0.85rem",
  background: "rgba(13,18,32,0.92)",
  padding: "1.15rem 1.25rem",
  display: "grid",
  gap: "0.85rem",
};

const mono: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "var(--chi-body-dense)",
  lineHeight: 1.65,
  color: "#94A3B8",
  margin: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

/** Long reports are cut off rather than pushing every other card off the screen. */
const DETAIL_LIMIT = 12;

function DetailList({ rows }: { rows: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) return null;
  const shown = expanded ? rows : rows.slice(0, DETAIL_LIMIT);
  const hidden = rows.length - shown.length;

  return (
    <div style={{ display: "grid", gap: "0.4rem" }}>
      <ul style={{ ...mono, listStyle: "none", padding: 0, display: "grid", gap: "0.2rem" }}>
        {shown.map((row, index) => <li key={index}>{row}</li>)}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{ justifySelf: "start", padding: 0, border: "none", background: "none", color: "#00D4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.76rem", fontWeight: 600, cursor: "pointer" }}
        >
          Show {hidden} more
        </button>
      )}
    </div>
  );
}

function ManualZoomTransferPanel({ isOwner, onDone }: { isOwner: boolean; onDone: () => void }) {
  const [zoomMeetingId, setZoomMeetingId] = useState("");
  const [preview, setPreview] = useState<{
    eventSlug: string;
    zoomMeetingId: string;
    summary: string;
    rows: string[];
    approvalToken: string;
  } | null>(null);
  const [result, setResult] = useState<{ summary: string; rows: string[] } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const previewMutation = trpc.maintenance.manualZoomTransferPreview.useMutation({
    onSuccess: data => { setPreview(data); setResult(null); setFailure(null); },
    onError: error => { setPreview(null); setFailure(error.message); },
  });
  const runMutation = trpc.maintenance.manualZoomTransferRun.useMutation({
    onSuccess: data => { setResult(data); setPreview(null); setFailure(null); onDone(); },
    onError: error => setFailure(error.message),
  });
  const busy = previewMutation.isPending || runMutation.isPending;

  return (
    <section style={{ ...panel, borderColor: "rgba(0,212,255,0.2)" }} aria-labelledby="manual-zoom-transfer-heading">
      <header style={{ display: "grid", gap: "0.35rem" }}>
        <h3 id="manual-zoom-transfer-heading" style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.98rem", fontWeight: 700 }}>
          Publish one linked Zoom recording
        </h3>
        <p style={{ ...mono, color: "#94A3B8" }}>
          Enter a Zoom meeting ID to find its current event link on this site. The recording will be queued for an unlisted YouTube upload only after you review the match.
        </p>
      </header>

      {!isOwner && (
        <p role="note" style={{ ...mono, display: "flex", gap: "0.45rem", alignItems: "center", color: "#FCD34D" }}>
          <ShieldAlert size={14} aria-hidden="true" />
          This action uses the project owner&rsquo;s connected YouTube account and is available only in the owner&rsquo;s session.
        </p>
      )}

      <form
        onSubmit={event => {
          event.preventDefault();
          setResult(null);
          setFailure(null);
          setPreview(null);
          previewMutation.mutate({ zoomMeetingId });
        }}
        style={{ display: "flex", flexWrap: "wrap", gap: "0.65rem", alignItems: "flex-end" }}
      >
        <div style={{ display: "grid", gap: "0.35rem", minWidth: "min(100%, 16rem)", flex: "1 1 22rem" }}>
          <label htmlFor="manual-zoom-meeting-id" style={{ color: "#CBD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 700 }}>
            Zoom meeting ID
          </label>
          <input
            id="manual-zoom-meeting-id"
            value={zoomMeetingId}
            onChange={event => setZoomMeetingId(event.target.value)}
            disabled={!isOwner || busy}
            placeholder="e.g. 123 4567 8901"
            inputMode="numeric"
            autoComplete="off"
            aria-describedby="manual-zoom-transfer-help"
            style={{ minHeight: 40, width: "100%", boxSizing: "border-box", padding: "0.55rem 0.7rem", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "0.55rem", background: "rgba(255,255,255,0.04)", color: "#F8FAFC", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.82rem" }}
          />
          <span id="manual-zoom-transfer-help" style={{ color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "0.72rem" }}>
            Use the ID from the site&rsquo;s linked Zoom meeting, not a Zoom account-wide search.
          </span>
        </div>
        <button
          type="submit"
          disabled={!isOwner || busy || !zoomMeetingId.trim()}
          style={{ display: "inline-flex", justifyContent: "center", alignItems: "center", gap: "0.4rem", minHeight: 40, flex: "0 1 auto", padding: "0.5rem 0.85rem", border: "1px solid rgba(0,212,255,0.4)", borderRadius: "0.55rem", background: "rgba(0,212,255,0.1)", color: "#67E8F9", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap" }}
        >
          {previewMutation.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : "Preview match"}
        </button>
      </form>

      {failure && <p role="alert" style={{ ...mono, color: "#FCA5A5" }}>{failure}</p>}

      {preview && (
        <div style={{ border: "1px solid rgba(245,166,35,0.3)", borderRadius: "0.6rem", background: "rgba(245,166,35,0.07)", padding: "0.8rem 0.9rem", display: "grid", gap: "0.6rem" }}>
          <p style={{ margin: 0, color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700 }}>
            Review this site-linked event before queueing:
          </p>
          <p style={{ margin: 0, color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: "0.8rem" }}>{preview.summary}</p>
          <DetailList rows={preview.rows} />
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => runMutation.mutate({ zoomMeetingId: preview.zoomMeetingId, approvalToken: preview.approvalToken })}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 38, padding: "0.45rem 0.95rem", border: "none", borderRadius: "0.55rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
            >
              {runMutation.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
              Queue this recording
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPreview(null)}
              style={{ minHeight: 38, padding: "0.45rem 0.95rem", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "0.55rem", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {result && (
        <div style={{ border: "1px solid rgba(34,197,94,0.3)", borderRadius: "0.6rem", background: "rgba(34,197,94,0.07)", padding: "0.8rem 0.9rem", display: "grid", gap: "0.5rem" }}>
          <p style={{ margin: 0, color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700 }}>Done. {result.summary}</p>
          <DetailList rows={result.rows} />
        </div>
      )}
    </section>
  );
}

type Job = {
  id: string;
  title: string;
  impact: string;
  kind: string;
  ownerOnly: boolean;
  runnable: boolean;
  previewable: boolean;
  blocked: boolean;
  category: string;
  scan: { state: JobState; count: number; summary: string; detail: string[] };
};

function JobCard({ job, onDone }: { job: Job; onDone: () => void }) {
  /*
   * A write is never one click. The preview is fetched first and the confirm
   * button does not exist until its result is on screen, so the operator cannot
   * approve a change they have not been shown.
   */
  const [preview, setPreview] = useState<{ summary: string; rows: string[]; approvalToken?: string } | null>(null);
  const [result, setResult] = useState<{ summary: string; rows: string[] } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /*
   * A preview describes one particular scan. When the scan moves underneath it --
   * a re-scan, or the refetch that follows another job's run -- the panel is
   * dropped rather than left on screen, because an operator approving a write is
   * entitled to assume the rows listed above the button are the current ones.
   */
  const [logOpen, setLogOpen] = useState(false);
  const signature = `${job.scan.state}:${job.scan.count}:${job.scan.summary}`;
  const lastSignature = useRef(signature);
  useEffect(() => {
    if (lastSignature.current === signature) return;
    lastSignature.current = signature;
    setPreview(null);
  }, [signature]);

  const previewMutation = trpc.maintenance.preview.useMutation({
    onSuccess: data => { setPreview(data); setFailure(null); },
    onError: err => setFailure(err.message),
  });
  const runMutation = trpc.maintenance.run.useMutation({
    onSuccess: data => { setResult(data); setPreview(null); setFailure(null); onDone(); },
    onError: err => setFailure(err.message),
  });

  const style = STATE_STYLE[job.scan.state];
  const busy = previewMutation.isPending || runMutation.isPending;

  return (
    <article style={panel} aria-labelledby={`job-${job.id}`}>
      <header style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: "14rem" }}>
          <h3 id={`job-${job.id}`} style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.95rem", fontWeight: 700 }}>
            {job.title}
          </h3>
          <p style={{ margin: "0.3rem 0 0", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-dense)", lineHeight: 1.6 }}>
            {job.impact}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
          <span style={{ padding: "0.2rem 0.5rem", borderRadius: "0.35rem", border: "1px solid rgba(255,255,255,0.12)", color: "#64748B", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {KIND_LABEL[job.kind] ?? job.kind}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", padding: "0.2rem 0.55rem", borderRadius: "0.35rem", background: style.bg, border: `1px solid ${style.border}`, color: style.color, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.7rem", fontWeight: 700 }}>
            <StateIcon state={job.scan.state} />
            {job.scan.count > 0 ? job.scan.count : style.label}
          </span>
        </div>
      </header>

      <p style={{ margin: 0, color: style.color, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.82rem", fontWeight: 600 }}>
        {job.scan.summary}
      </p>

      <DetailList rows={job.scan.detail} />

      {job.blocked && (
        <p role="note" style={{ ...mono, display: "flex", gap: "0.45rem", alignItems: "center", color: "#FCD34D" }}>
          <ShieldAlert size={14} aria-hidden="true" />
          Uses the project owner&rsquo;s own credentials, so it needs the owner&rsquo;s session.
        </p>
      )}

      {failure && (
        <p role="alert" style={{ ...mono, color: "#FCA5A5" }}>{failure}</p>
      )}

      {preview && (
        <div style={{ border: "1px solid rgba(245,166,35,0.3)", borderRadius: "0.6rem", background: "rgba(245,166,35,0.07)", padding: "0.8rem 0.9rem", display: "grid", gap: "0.6rem" }}>
          <p style={{ margin: 0, color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700 }}>
            Nothing has changed yet. This is what running it would do:
          </p>
          <p style={{ margin: 0, color: "#E2E8F0", fontFamily: "'Inter', sans-serif", fontSize: "0.8rem" }}>{preview.summary}</p>
          <DetailList rows={preview.rows} />
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            {preview.rows.length > 0 && (
            <button
              type="button"
              disabled={busy}
               onClick={() => runMutation.mutate({ jobId: job.id, approvalToken: preview.approvalToken })}
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 38, padding: "0.45rem 0.95rem", border: "none", borderRadius: "0.55rem", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "white", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
            >
              {runMutation.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
              Apply {preview.summary.split(" ")[0]} change{preview.summary.startsWith("1 ") ? "" : "s"}
            </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => setPreview(null)}
              style={{ minHeight: 38, padding: "0.45rem 0.95rem", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "0.55rem", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer" }}
            >
              {preview.rows.length > 0 ? "Cancel" : "Close"}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div style={{ border: "1px solid rgba(34,197,94,0.3)", borderRadius: "0.6rem", background: "rgba(34,197,94,0.07)", padding: "0.8rem 0.9rem", display: "grid", gap: "0.5rem" }}>
          <p style={{ margin: 0, color: "#86EFAC", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700 }}>Done. {result.summary}</p>
          <DetailList rows={result.rows} />
        </div>
      )}

      {/*
        Every card gets an action, even a check that has nothing to run.

        A check-only job used to end at its one-line summary with no way further
        in: the full findings existed but the card offered no button to reach them,
        so a count of twelve was the end of the trail. The log is that button, and
        it is always present so no section is a dead end.
      */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
        {job.runnable && !preview && !job.blocked && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setResult(null);
              if (job.previewable) previewMutation.mutate({ jobId: job.id });
              else runMutation.mutate({ jobId: job.id });
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 38, padding: "0.45rem 0.95rem", border: "1px solid rgba(233,30,140,0.45)", borderRadius: "0.55rem", background: "rgba(233,30,140,0.12)", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, cursor: busy ? "wait" : "pointer" }}
          >
            {previewMutation.isPending ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
            {job.previewable ? "Preview what this would do" : "Run this job"}
          </button>
        )}
        <button
          type="button"
          aria-expanded={logOpen}
          onClick={() => setLogOpen(open => !open)}
          style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 38, padding: "0.45rem 0.9rem", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "0.55rem", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer" }}
        >
          <ScrollText size={14} aria-hidden="true" />
          {logOpen ? "Hide log" : "View log"}
        </button>
      </div>

      {logOpen && (
        <div style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: "0.6rem", background: "rgba(0,0,0,0.25)", padding: "0.8rem 0.9rem", display: "grid", gap: "0.55rem" }}>
          <p style={{ ...mono, margin: 0, color: "#7DEAFF" }}>
            {job.id} · {job.kind} · {job.scan.state}{job.scan.count > 0 ? ` · ${job.scan.count} found` : ""}
          </p>
          {/* Untruncated on purpose: this is the view someone opened to see everything. */}
          {job.scan.detail.length
            ? <ul style={{ ...mono, listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.2rem" }}>
                {job.scan.detail.map((row, index) => <li key={index}>{row}</li>)}
              </ul>
            : <p style={{ ...mono, margin: 0 }}>This check recorded no individual findings.</p>}
          {result?.rows?.length ? (
            <>
              <p style={{ ...mono, margin: 0, color: "#86EFAC" }}>Last run</p>
              <ul style={{ ...mono, listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "0.2rem" }}>
                {result.rows.map((row, index) => <li key={index}>{row}</li>)}
              </ul>
            </>
          ) : null}
        </div>
      )}
    </article>
  );
}

export default function MaintenanceView() {
  const jobs = trpc.maintenance.list.useQuery(undefined, { refetchOnWindowFocus: false });
  const [category, setCategory] = useState<string>(MAINTENANCE_CATEGORIES[0].id);

  const all = jobs.data?.jobs ?? [];
  /*
   * Counted per tab from what the scan actually found, so the tab bar carries the
   * same information the cards do. An operator opening this page wants to know
   * which group needs them before reading any card in it.
   */
  const countFor = (id: string) =>
    all.filter(job => job.category === id && job.scan.state !== "ok").length;
  const shownJobs = all.filter(job => job.category === category);

  return (
    <div style={{ flex: 1, padding: "clamp(1.25rem, 3vw, 2rem)", display: "grid", gap: "1.1rem", alignContent: "start" }}>
      <header style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.4rem,2.6vw,1.9rem)" }}>Maintenance</h2>
          <p style={{ margin: "0.25rem 0 0", color: "#64748B", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-dense)" }}>
            Background work that has no other home. Every card says what running it does before you run it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => jobs.refetch()}
          disabled={jobs.isFetching}
          style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", minHeight: 38, padding: "0.45rem 0.9rem", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "0.55rem", background: "transparent", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.8rem", fontWeight: 600, cursor: jobs.isFetching ? "wait" : "pointer" }}
        >
          {jobs.isFetching ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />}
          Re-scan
        </button>
      </header>

      {/*
        Grouped rather than one long column. Eight jobs spanning third-party
        credentials, artwork and database shape shared a single scroll, so finding
        the one that needed attention meant reading all of them.
      */}
      <div role="tablist" aria-label="Maintenance categories" className="crm-underline-tabs">
        {MAINTENANCE_CATEGORIES.map(entry => {
          const isActive = entry.id === category;
          const needsAttention = countFor(entry.id);
          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setCategory(entry.id)}
              data-active={isActive || undefined}
            >
              {entry.label}
              {/* Only when there is something to say — a zero badge is noise. */}
              {needsAttention > 0 && (
                <span style={{ padding: ".05rem .35rem", borderRadius: ".35rem", background: "rgba(252,211,77,.14)", border: "1px solid rgba(252,211,77,.35)", color: "#FCD34D", fontSize: ".68rem", fontWeight: 800 }}>
                  {needsAttention}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/*
        Reported private messages sit here rather than on Messages. Messages is for
        composing outreach; reviewing a report is moderation work that arrives on
        its own schedule and belongs with the other queues that need attention.
      */}
      <MemberMessageReportsPanel />
      {jobs.isLoading && (
        <p style={{ ...mono, display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <Loader2 size={15} className="animate-spin" aria-hidden="true" /> Scanning&hellip;
        </p>
      )}

      {jobs.error && <p role="alert" style={{ ...mono, color: "#FCA5A5" }}>{jobs.error.message}</p>}

      <ManualZoomTransferPanel isOwner={jobs.data?.isOwner === true} onDone={() => jobs.refetch()} />

      {shownJobs.map(job => (
        <JobCard key={job.id} job={job as Job} onDone={() => jobs.refetch()} />
      ))}
    </div>
  );
}
